/**
 * A teacher's recent class sessions, each linking to its results page.
 *
 * Results were reachable only in the moment a teacher pressed End session;
 * close that tab and a session's marks had no way back in.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { getRecentTeacherSessions, type SessionSummaryRow } from '../../services/performanceService';
import { AnalyticsEmptyState, AnalyticsPanel, TableSkeleton } from './AnalyticsPanel';

export function RecentSessions({ teacherUid, classNames }: { teacherUid: string; classNames: Record<string, string> }) {
  const [sessions, setSessions] = useState<SessionSummaryRow[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getRecentTeacherSessions(teacherUid)
      .then((rows) => !cancelled && setSessions(rows))
      .catch((err) => {
        console.warn('[RecentSessions] could not load sessions:', err);
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [teacherUid]);

  return (
    <AnalyticsPanel title="Recent class sessions" description="Open a session to see its results">
      {failed ? (
        <p className="py-4 text-sm text-muted-foreground">Recent sessions couldn’t be loaded.</p>
      ) : sessions === null ? (
        <TableSkeleton rows={3} />
      ) : sessions.length === 0 ? (
        <AnalyticsEmptyState compact title="No class sessions yet" description="Start a session to teach a lesson live; its results will be listed here." />
      ) : (
        <ul className="divide-y divide-border">
          {sessions.map((s) => (
            <li key={s.id}>
              <Link to={`/class-session/${s.id}/results`} className="flex items-center gap-3 py-2.5 hover:text-foreground">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{s.lessonTitle || 'Class session'}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {[
                      s.classId ? classNames[s.classId] ?? 'Class' : null,
                      s.startedAt ? new Date(s.startedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                {s.status !== 'ended' && <span className="text-[10px] font-semibold uppercase tracking-wide text-primary">Live</span>}
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </AnalyticsPanel>
  );
}
